import React, { useCallback, useEffect, useRef, useState } from "react";
import { Container } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import Header from "@/components/layout/header";
import Sidebar from "@/components/layout/sidebar";
import MobileBottomNav from "@/components/navigation/mobileBottomNav";
import ScrollTopButton from "@/components/ui/scrollTopButton";
import CartModalContainer from "@/components/cart/cartModalContainer";
import { subscribeOpenCart } from "@/components/cart/helpers/cartUiEvents";
import DishDetailsModalContainer from "@/pages/menuItemsPage/components/dishDetailsModalContainer";
import { useDishDetailsModalRouting } from "@/pages/menuItemsPage/helpers/hooks/useDishDetailsModalRouting";
import ToastComponent from "@/components/ui/toastComponent";
import { useToast } from "@/hooks/useToast";
import { subscribeToast } from "@/components/ui/toastComponent/helpers/toastUiEvents";

interface IWrapperProps {
  children: React.ReactNode;
}

const LayoutWrapper: React.FC<IWrapperProps> = ({ children }) => {
  const location = useLocation();
  const reopenCartAfterDetailsRef = useRef(false);

  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const { showToast, toggleShowToast, setShowToast, toastText } = useToast();

  const { dishId, categoryId, openDishDetailsModal, closeDishDetailsModal } =
    useDishDetailsModalRouting();

  const openMobileSidebar = useCallback(() => {
    setIsMobileSidebarOpen(true);
  }, []);
  const closeMobileSidebar = useCallback(() => {
    setIsMobileSidebarOpen(false);
  }, []);

  const openCart = useCallback(() => {
    reopenCartAfterDetailsRef.current = false;
    setIsMobileSidebarOpen(false);
    setIsCartOpen(true);
  }, []);

  const closeCart = useCallback(() => {
    reopenCartAfterDetailsRef.current = false;
    setIsCartOpen(false);
  }, []);

  const closeCartForEdit = useCallback(() => {
    setIsCartOpen(false);
  }, []);

  const handleEditDishFromCart = useCallback(
    (dishIdToEdit: number) => {
      reopenCartAfterDetailsRef.current = true;
      closeCartForEdit();
      openDishDetailsModal(dishIdToEdit);
    },
    [closeCartForEdit, openDishDetailsModal]
  );

  useEffect(() => {
    if (dishId != null) return;
    if (!reopenCartAfterDetailsRef.current) return;
    reopenCartAfterDetailsRef.current = false;
    openCart();
  }, [dishId, openCart]);

  useEffect(() => {
    setIsCartOpen(false);
    setIsMobileSidebarOpen(false);
  }, [location.pathname]);

  const reopenCartAfterConfirmRef = useRef(false);

  const handleConfirmDeleteOpen = useCallback(() => {
    reopenCartAfterConfirmRef.current = true;
    setIsCartOpen(false);
  }, []);

  const handleConfirmDeleteClose = useCallback(() => {
    if (!reopenCartAfterConfirmRef.current) return;
    reopenCartAfterConfirmRef.current = false;
    openCart();
  }, [openCart]);

  useEffect(() => {
    return subscribeOpenCart(openCart);
  }, [openCart]);

  useEffect(() => {
    return subscribeToast((text: string) => {
      toggleShowToast(text);
    });
  }, [toggleShowToast]);

  return (
    <>
      <Header
        onMobileSidebarOpen={openMobileSidebar}
        onOpenSelected={openCart}
      />
      <Sidebar
        show={isMobileSidebarOpen}
        onHide={closeMobileSidebar}
        onOpenSelected={openCart}
      />
      <main
        id="app-scroll-container"
        className="app-main position-fixed start-0 end-0 overflow-auto"
      >
        <Container>{children}</Container>
      </main>
      <MobileBottomNav onOpenSelected={openCart} />
      <ScrollTopButton />
      <CartModalContainer
        isOpen={isCartOpen}
        onClose={closeCart}
        onEditDish={handleEditDishFromCart}
        onConfirmDeleteOpen={handleConfirmDeleteOpen}
        onConfirmDeleteClose={handleConfirmDeleteClose}
      />
      <DishDetailsModalContainer
        isOpen={dishId != null}
        dishId={dishId}
        categoryId={categoryId}
        onClose={closeDishDetailsModal}
      />
      <ToastComponent
        show={showToast}
        setShow={setShowToast}
        text={toastText}
      />
    </>
  );
};

export default LayoutWrapper;
